import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { setDemoHandler, tourAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import {
  TourContext, emptyProgress, readLocal, writeLocal, fromServer, applyPatch, defaultRouteFor, isUnfinished, resumeStepOf,
} from './tourCore';
import { ROUTES } from './tourSteps';
import { buildFixtures, demoRespond, DEMO_MSG, DEMO_PROFILE_ID, DEMO_SEED } from './tourFixtures';
import TourOverlay, { TourInvite, TourDone } from './TourOverlay';

// The demo 客户工作台 is the real ClientCrmModal in `demo` mode (lazy: it is a large module).
const ClientCrmModal = lazy(() => import('../profiles/ClientCrmModal'));

// 操作引导 — guided tours over the real screens in 示例模式.
//
// While a route runs:
//  - services/api.js routes every request through demoRespond(): GETs for the sample member / team
//    come from tourFixtures.js, writes are refused with "示例模式：不会保存" (only the tour's own
//    progress PUT goes out). The toast is shown here; the component's own error toast right after
//    a refused write is swallowed.
//  - steps that happen in the workspace render ClientCrmModal demo on top, driven by `demoView`;
//    page steps navigate (/profiles, /service-supervision) and those pages read `demo` / `svTab`.
//  - a click on a step's clickTarget is caught (capture phase) and advances instead.
//  - Esc / 退出引导 stop the run, drop the fixtures and go back where the user started.
// Progress: GET/PUT /service/tour, mirrored to localStorage so it still works where the API 404s.

const SUPPRESS_MS = 1500;
const USER_INPUT_MS = 2500;

export default function TourProvider({ children }) {
  const { user } = useAuth();
  const { language } = useLanguage();
  const isZh = language !== 'en';
  const navigate = useNavigate();
  const location = useLocation();
  const uid = user && user.id;

  const [progress, setProgress] = useState(() => readLocal(uid) || emptyProgress());
  const progressRef = useRef(progress);
  progressRef.current = progress;
  const defaultRoute = defaultRouteFor(user);
  const defaultRouteRef = useRef(defaultRoute);
  defaultRouteRef.current = defaultRoute;
  const loadedRef = useRef(false);
  const pendingInviteRef = useRef(false);

  // run: { route, idx, fx, origin, viewN } | null
  const [run, setRun] = useState(null);
  const runRef = useRef(null);
  runRef.current = run;
  const [done, setDone] = useState(null);       // route whose done screen is showing
  const [invite, setInvite] = useState(false);
  const [introTick, setIntroTick] = useState(0);
  const invitedRef = useRef(false);
  const evalInvite = useCallback(() => {
    if (invitedRef.current || runRef.current) return;
    const p = progressRef.current[defaultRouteRef.current];
    if (!p || p.completed || p.dismissed) return;
    invitedRef.current = true;
    setInvite(true);
  }, []);
  const blockedAtRef = useRef(0);
  const inputAtRef = useRef(0);
  const realToastRef = useRef(null);

  // ── progress ──
  useEffect(() => {
    if (!uid) return undefined;
    let alive = true;
    loadedRef.current = false;
    const local = readLocal(uid);
    if (local) { progressRef.current = local; setProgress(local); }
    const loaded = () => {
      loadedRef.current = true;
      if (pendingInviteRef.current) { pendingInviteRef.current = false; evalInvite(); }
    };
    tourAPI.get()
      .then((r) => {
        if (!alive || !r || !r.data || !r.data.routes) return;
        const server = fromServer(r.data.routes);
        // A step reached offline (before the deploy) is not thrown away.
        ['coach', 'supervisor'].forEach((k) => {
          if (local && local[k] && !server[k].completed && Number(local[k].last_step) > Number(server[k].last_step)) {
            server[k].last_step = local[k].last_step;
          }
        });
        progressRef.current = server;
        setProgress(server);
        writeLocal(uid, server);
      })
      .catch(() => { /* 404 before deploy → localStorage only */ })
      .then(() => { if (alive) loaded(); });
    return () => { alive = false; };
  }, [uid, evalInvite]);

  const save = useCallback((route, patch) => {
    const next = applyPatch(progressRef.current, route, patch);
    progressRef.current = next;
    setProgress(next);
    writeLocal(uid, next);
    tourAPI.save(route, patch)
      .then((r) => {
        if (r && r.data && r.data.routes) {
          const server = fromServer(r.data.routes);
          progressRef.current = server;
          setProgress(server);
          writeLocal(uid, server);
        }
      })
      .catch(() => { /* kept locally */ });
  }, [uid]);

  const reach = useCallback((route, stepNo) => {
    const p = progressRef.current[route];
    if (p && !p.completed && stepNo > (Number(p.last_step) || 0)) save(route, { last_step: stepNo });
  }, [save]);

  // ── demo data source ──
  const installDemo = useCallback((fx) => {
    setDemoHandler((config) => {
      const out = demoRespond(fx, config);
      if (out && out.block) {
        const now = Date.now();
        blockedAtRef.current = now;
        if (now - inputAtRef.current < USER_INPUT_MS) {
          const show = realToastRef.current || toast.info;
          try { show(DEMO_MSG, { toastId: 'tour-demo-nosave', autoClose: 2200 }); } catch (e) { /* ignore */ }
        }
      }
      return out;
    });
  }, []);

  const demoOn = !!run;
  useEffect(() => {
    if (!demoOn) { setDemoHandler(null); return undefined; }
    // Swallow the component's own error / success toast right after a refused write.
    const keys = ['error', 'success', 'warn', 'warning', 'info'];
    const orig = {};
    keys.forEach((k) => {
      if (typeof toast[k] !== 'function') return;
      orig[k] = toast[k];
      toast[k] = (...args) => (Date.now() - blockedAtRef.current < SUPPRESS_MS ? undefined : orig[k](...args));
    });
    realToastRef.current = orig.info || null;
    const onInput = () => { inputAtRef.current = Date.now(); };
    window.addEventListener('pointerdown', onInput, true);
    window.addEventListener('keydown', onInput, true);
    document.body.classList.add('mm-tour-demo');
    return () => {
      keys.forEach((k) => { if (orig[k]) toast[k] = orig[k]; });
      realToastRef.current = null;
      window.removeEventListener('pointerdown', onInput, true);
      window.removeEventListener('keydown', onInput, true);
      document.body.classList.remove('mm-tour-demo');
      setDemoHandler(null);
    };
  }, [demoOn]);

  // Leaving the dashboard (logout) never leaves the handler behind.
  useEffect(() => () => setDemoHandler(null), []);

  // ── run control ──
  const start = useCallback((route = defaultRoute, stepNo = 1) => {
    const r = ROUTES[route] ? route : 'coach';
    const steps = ROUTES[r];
    const idx = Math.min(steps.length - 1, Math.max(0, (Number(stepNo) || 1) - 1));
    const fx = buildFixtures(r);
    installDemo(fx); // before the pages re-fetch with demo on
    const cur = runRef.current;
    setInvite(false);
    setDone(null);
    setRun({
      route: r, idx, fx, viewN: ((cur && cur.viewN) || 0) + 1,
      origin: cur ? cur.origin : `${location.pathname}${location.search || ''}`,
    });
    reach(r, idx + 1);
  }, [defaultRoute, installDemo, location.pathname, location.search, reach]);

  const goTo = useCallback((idx) => {
    const cur = runRef.current;
    if (!cur) return;
    const steps = ROUTES[cur.route];
    if (idx < 0) return;
    if (idx >= steps.length) {
      // finished
      save(cur.route, { completed: true });
      setDemoHandler(null);
      setRun(null);
      setDone(cur.route);
      if (cur.route === 'coach' && cur.origin && cur.origin !== `${location.pathname}${location.search || ''}`) navigate(cur.origin);
      return;
    }
    setRun({ ...cur, idx, viewN: cur.viewN + 1 });
    reach(cur.route, idx + 1);
  }, [save, reach, navigate, location.pathname, location.search]);

  const next = useCallback(() => { const c = runRef.current; if (c) goTo(c.idx + 1); }, [goTo]);
  const prev = useCallback(() => { const c = runRef.current; if (c) goTo(c.idx - 1); }, [goTo]);

  const exit = useCallback(() => {
    const cur = runRef.current;
    setDemoHandler(null);
    setRun(null);
    if (cur && cur.origin && cur.origin !== `${location.pathname}${location.search || ''}`) navigate(cur.origin);
  }, [navigate, location.pathname, location.search]);

  // 跳过引导 on step 1: stop, and do not invite again.
  const skip = useCallback(() => {
    const cur = runRef.current;
    if (cur) {
      const p = progressRef.current[cur.route];
      if (p && !p.completed) save(cur.route, { dismissed: true });
    }
    exit();
  }, [exit, save]);

  const step = run ? ROUTES[run.route][run.idx] : null;
  const screen = (step && step.screen) || {};
  const runRoute = run ? run.route : null;
  const runIdx = run ? run.idx : -1;
  const stepPath = screen.path || null;
  const clickTarget = (step && step.clickTarget) || null;
  const overlayOpen = !!run || !!done || invite;

  // Navigate to the step's page.
  const pathname = location.pathname;
  useEffect(() => {
    if (!runRoute || !stepPath) return;
    if (pathname !== stepPath) navigate(stepPath);
  }, [runRoute, runIdx, stepPath]);

  // Click on the step's clickTarget = 下一步 (and the screen's own handler does not run).
  useEffect(() => {
    if (!clickTarget) return undefined;
    const sel = `[data-tour="${clickTarget}"]`;
    const onClick = (e) => {
      const hit = e.target && e.target.closest && e.target.closest(sel);
      if (!hit) return;
      e.preventDefault();
      e.stopPropagation();
      next();
    };
    window.addEventListener('click', onClick, true);
    return () => window.removeEventListener('click', onClick, true);
  }, [clickTarget, runRoute, runIdx, next]);

  // Esc exits (before any modal's own Esc handler sees it).
  useEffect(() => {
    if (!overlayOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      if (runRef.current) exit();
      else { setDone(null); setInvite(false); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [overlayOpen, exit]);

  // ── entry points ──
  const maybeInvite = useCallback(() => {
    if (invitedRef.current || runRef.current) return;
    if (!loadedRef.current) { pendingInviteRef.current = true; return; }
    evalInvite();
  }, [evalInvite]);
  const openInvite = useCallback(() => setInvite(true), []);
  const openIntro = useCallback(() => setIntroTick((n) => n + 1), []);
  // A click on a sample member outside the clickTarget (e.g. another chip) on a step that
  // expects one → treat as the same click.
  const demoMemberClick = useCallback(() => {
    const cur = runRef.current;
    const st = cur ? ROUTES[cur.route][cur.idx] : null;
    if (st && st.clickTarget) next();
  }, [next]);

  const unfinished = isUnfinished(progress[defaultRoute]);

  const value = useMemo(() => ({
    available: true,
    active: !!run,
    demo: !!run,
    route: run ? run.route : defaultRoute,
    stepIndex: run ? run.idx : -1,
    step,
    total: run ? ROUTES[run.route].length : 0,
    svTab: run ? screen.sv || null : null,
    viewN: run ? run.viewN : 0,
    fixtures: run ? run.fx : null,
    progress, defaultRoute, unfinished, introTick,
    start, next, prev, exit, skip, openInvite, maybeInvite, demoMemberClick, openIntro,
    resumeStep: (route) => resumeStepOf(progress[route], route),
  }), [run, step, screen.sv, progress, defaultRoute, unfinished, introTick, start, next, prev, exit, skip, openInvite, maybeInvite, demoMemberClick, openIntro]);

  const ws = run && screen.ws ? screen.ws : null;
  const demoView = ws ? { ...ws, n: run.viewN, route: run.route } : null;

  return (
    <TourContext.Provider value={value}>
      {children}
      {ws && (
        <Suspense fallback={null}>
          <ClientCrmModal key={`tour-${run.route}`} demo demoView={demoView} profileId={DEMO_PROFILE_ID} seed={DEMO_SEED}
            isZh={isZh} onClose={exit} />
        </Suspense>
      )}
      {run && step && (
        <TourOverlay route={run.route} idx={run.idx} step={step} total={ROUTES[run.route].length} stepKey={`${run.route}-${run.idx}-${run.viewN}`}
          onNext={next} onPrev={prev} onExit={exit} onSkip={skip} />
      )}
      {invite && !run && (
        <TourInvite defaultRoute={defaultRoute} progress={progress}
          onStart={(r) => start(r, resumeStepOf(progress[r], r))}
          onLater={() => { setInvite(false); save(defaultRoute, { dismissed: true }); }} />
      )}
      {done && !run && (
        <TourDone route={done} weekly={!!(progress.supervisor && progress.supervisor.weekly_reminder)}
          onAgain={() => start(done, 1)}
          onClose={(opts = {}) => {
            const r = done;
            setDone(null);
            if (r === 'supervisor') {
              if (opts.weekly !== undefined && !!opts.weekly !== !!(progressRef.current.supervisor || {}).weekly_reminder) {
                save('supervisor', { weekly_reminder: !!opts.weekly });
              }
              if (opts.go) navigate('/service-supervision');
            }
          }} />
      )}
    </TourContext.Provider>
  );
}
